// @ts-strict-ignore
import React, { useContext, useState } from 'react';
import {
  Box,
  Button,
  ButtonVariant,
  makeInitialTableState,
  Modal,
  MultiSelect,
  Table,
  useSnackBar,
} from '@usealloy/component-library';
import {
  trxStatusOptions, TRX_COLUMNS, TrxStatusOption, TrxStatusSingleOption,
} from './constants';
import { typhonRoutes } from '~/helpers/routes';
import { typhonAjaxFetch } from '~/helpers/api';
import { SELECTABLE_TRX_STATUSES } from '../status/status';
import log from '~/helpers/logging';
import { mapReasonToOption } from '../status/reason';
import { useQuery } from '@tanstack/react-query';
import type { TableState } from '@tanstack/react-table';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { loadInvestigation } from '~/investigations/redux/thunks';
import { UserContext } from '~/dashboard/context';
import { selectInvestigationDetail } from '~/investigations/redux/selectors';

export type TypeOption = { value: string; label: string };

export type TransactionStatusUpdateModalProps = {
  closeModal: () => void;
  isOpen: boolean;
  transactions: any;
  investigationToken?: string | null;
  setSelectedTransactions: Function;
  trxTableState: TableState;
  setTrxTableState: Function;
};

const TransactionStatusUpdateModal: React.FC<TransactionStatusUpdateModalProps> = ({
  isOpen,
  closeModal,
  transactions,
  investigationToken = null,
  setSelectedTransactions,
  trxTableState,
  setTrxTableState,
}) => {
  const appDispatch = useAppDispatch();
  const { featureFlags, typhonDomain } = useContext(UserContext);
  const investigation = useAppSelector(selectInvestigationDetail);
  const [tableState, setTableState] = useState(makeInitialTableState());
  const [selectedTrxStatus, setSelectedTrxStatus] = useState<TypeOption | null>(null);
  const [selectedTrxReason, setSelectedTrxReason] = useState<TypeOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const addSnackBar = useSnackBar();

  const useTrxDescriptions = () => useQuery({
    queryKey: ['trxDescriptions', typhonDomain],
    queryFn: async () => {
      const url = `${typhonDomain}${typhonRoutes.entities.transactions.descriptions}`;
      const trxReasonsByStatus = { Accepted: [], Rejected: [] };
      const { data } = await typhonAjaxFetch(url);
      data.trx_status_details.forEach(trxDescription => {
        if (SELECTABLE_TRX_STATUSES.includes(trxDescription.status)) {
          const reasonData = {
            key: trxDescription.status_detail_value.replace(/[^a-z0-9]/i, '_'),
            label: trxDescription.status_detail_label,
            value: trxDescription.status_detail_value,
          };
          trxReasonsByStatus[trxDescription.status].push(reasonData);
        }
      });
      return trxReasonsByStatus;
    },
    enabled: isOpen && !!typhonDomain,
    onError: error => {
      const url = `${typhonDomain}${typhonRoutes.entities.transactions.descriptions}`;
      log('error', `GET ${url} exception`, {}, error);
    },
    placeholderData: {
      Accepted: [{ key: 'passed_review', label: 'Passed Review', value: 'passed_review' }],
      Rejected: [{ key: 'failed_review', label: 'Failed Review', value: 'failed_review' }],
    },
  });

  const {
    data: trxStatusDescriptionsByStatus = { Accepted: [], Rejected: [] },
  } = useTrxDescriptions();

  const handleCloseModal = () => {
    setSelectedTrxStatus(null);
    setSelectedTrxReason(null);
    closeModal();
  };

  const handleTrxStatusUpdate = async () => {
    setIsSubmitting(true);
    const url = `${typhonDomain}${typhonRoutes.trx.patch.status}`;
    try {
      const trxUpdateStatusBody = {
        external_transaction_ids: transactions.map(trx => trx.external_transaction_identifier),
        new_status: selectedTrxStatus.value,
        new_status_detail: selectedTrxReason?.value,
        investigation_token: investigationToken,
      };
      await typhonAjaxFetch(url, {
        method: 'PATCH',
        body: JSON.stringify(trxUpdateStatusBody),
      });

      addSnackBar({
        type: 'success',
        body: 'Webhook sent successfully',
        title: '',
      });
      setSelectedTransactions([]);
      setTrxTableState({
        ...trxTableState,
        rowSelection: {},
      });
      handleCloseModal();
      await appDispatch(loadInvestigation({
        investigationToken: investigation.investigationToken,
        featureFlags,
        typhonDomain,
      }));
    } catch (error) {
      addSnackBar({
        type: 'danger',
        body: 'An unexpected error occurred sending the webhook, please try again.',
        title: '',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      data-testid="transaction-status-update-modal"
      header="Change Transaction Status"
      isOpen={isOpen}
      onClose={handleCloseModal}
      primaryAction={
        (
          <Button
            onClick={handleTrxStatusUpdate}
            variant={ButtonVariant.primary}
            disabled={!selectedTrxStatus}
            loading={isSubmitting}
          >
            Submit
          </Button>
        )
      }
      secondaryAction={
        (
          <Button
            onClick={handleCloseModal}
            variant={ButtonVariant.secondary}
          >
            Cancel
          </Button>
        )
      }
    >
      <Box gap={4} direction="column" style={{ padding: '24px 12px' }}>
        <Table
          columns={TRX_COLUMNS.filter(
            col => featureFlags?.addEvidenceToTrxsAndBas || col.id !== 'evidence_count',
          )}
          data={transactions}
          onTableStateChange={newState => {
            setTableState(newState);
          }}
          state={{
            ...tableState,
          }}
        />
      </Box>
      <Box gap={4} direction="row" style={{ padding: '0px 12px' }}>
        <MultiSelect
          label="Status"
          options={trxStatusOptions}
          placeholder="Select Status"
          onChange={(option: TypeOption) => {
            setSelectedTrxReason(null);
            setSelectedTrxStatus(option);
          }}
          selectedOptions={selectedTrxStatus}
          visibleSelectedOptions
          isMulti={false}
          required
          components={{
            Option: TrxStatusOption as any,
            SingleValue: TrxStatusSingleOption as any,
          }}
        />
        <MultiSelect
          isClearable
          label="Reason"
          options={mapReasonToOption(trxStatusDescriptionsByStatus, selectedTrxStatus?.value)}
          placeholder="Select a Reason"
          onChange={(option: TypeOption) => {
            setSelectedTrxReason(option);
          }}
          selectedOptions={selectedTrxReason}
          visibleSelectedOptions
          isMulti={false}
          isDisabled={!selectedTrxStatus}
        />
      </Box>
    </Modal>
  );
};

export default TransactionStatusUpdateModal;
